/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { useEffect, useCallback, useRef, useMemo } from "react";
import { Spin, Button, Flex, Alert } from "@duxton-web/ui";
import { ReactGrid, Row, Column, DefaultCellTypes, CellChange } from "@silevis/reactgrid";
import "@silevis/reactgrid/styles.css";

import "./generic-grid.css";
import ImageCellTemplate, { ImageCell } from "./template/ImageCellTemplate";
import { AsyncValidateCellProps, ValidationErrors, DataRowConfig } from "./types";
import { generateChangesFromData, setTargetCellError } from "./utils";
import { useHandleErrors } from "./hooks/useHandleErrors";
import { useHandleChanges } from "./hooks/useHandleChanges";
import { useHandleFocusLocation } from "./hooks/useHandleFocusLocation";
import { useNavigateErrorCell } from "./hooks/useNavigateErrorCell";
import { useHandleRows } from "./hooks/useHandleRows";
import { useBrowserCompatibility } from "./hooks/useBrowserCompatibility";

/* eslint-disable react-hooks/exhaustive-deps */

const LoadingOverlay = () => (
  <div className="loading-overlay">
    <Spin size="large" />
  </div>
);

const BrowserCompatibilityAlert = () => (
  <div className="generic-grid">
    <Alert
      type="error"
      message="Browser Not Supported"
      description="Your browser version is not supported"
      showIcon
    />
  </div>
);

interface GridControlsProps {
  onAdd: () => void;
  onRemove: () => void;
  canRemove: boolean;
}
const GridControls = ({ onAdd, onRemove, canRemove }: GridControlsProps) => (
  <Flex gap={8} style={{ marginTop: 8 }}>
    <Button size="small" type="primary" onClick={onAdd}>Add</Button>
    {canRemove && (
      <Button size="small" danger onClick={onRemove}>Remove</Button>
    )}
  </Flex>
);

interface GenericGridProps<T extends { [key: string]: any }> {
  data: T[];
  columns: Column[];
  rows: (arg: DataRowConfig<T>) => Row<DefaultCellTypes | ImageCell>[];
  headerRow: Row;
  validator: (props: AsyncValidateCellProps) => Promise<{ newErrors: ValidationErrors }>;
  isValidateAtFirstLoad?: boolean;
  headerRowHeight?: number;
  dataRowHeight?: number;
  onDataChange: (data: T[]) => void;
  handleAddingRow?: (newData: T, id?: number) => void;
  defaultData: T;
  defaultColumnId: string;
  blockedColumnIds?: string[];
}
const GenericGrid = <T extends { [key: string]: any }>({ 
  data = [],
  columns,
  rows,
  headerRow,
  validator,
  isValidateAtFirstLoad = false,
  onDataChange,
  handleAddingRow,
  headerRowHeight = 25,
  dataRowHeight = 100,
  defaultData,
  defaultColumnId,
  blockedColumnIds,
}: GenericGridProps<T>): JSX.Element => {
  const { isBrowserCompatible } = useBrowserCompatibility();
  const { errorMessage, handleErrors, getErrorCells } = useHandleErrors();
  const { loading, handleChanges } = useHandleChanges(data, validator, onDataChange, handleErrors);
  const { focusState, setValidFocusLocation } = useHandleFocusLocation({ defaultColumnId, blockedColumnIds });
  const { moveToNextErrorCellOnEnter } = useNavigateErrorCell(focusState, columns, getErrorCells);
  const { removeRow, addRow } = useHandleRows(
    data, focusState, onDataChange, defaultData, defaultColumnId, handleAddingRow
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isFirstRender = useRef(true);

  // Memoize adjustedRows
  const adjustedRows = useMemo(() =>
    rows({ data, headerRow, errors: errorMessage }).map((row, idx) => ({
      ...row,
      height: idx === 0 ? headerRowHeight : dataRowHeight,
    })),
    [rows, data, headerRow, errorMessage, headerRowHeight, dataRowHeight]
  );

  // Validate data at first load
  const validateData = useCallback(async () => {
    const changes = generateChangesFromData<T>(data);
    const results = await Promise.all(
      changes.map(change => validator({ change, setTargetCellError }))
    );
    results.forEach(({ newErrors }) => handleErrors(newErrors));
  }, [validator, data, handleErrors]);

  useEffect(() => {
    if (isFirstRender.current && isValidateAtFirstLoad && data.length > 0) {
      validateData();
      isFirstRender.current = false;
    }
  }, [isValidateAtFirstLoad, data, validateData]);

  // Keyboard event listener
  useEffect(() => {
    const containerElement = containerRef.current;
    if (containerElement) {
      containerElement.addEventListener("keydown", moveToNextErrorCellOnEnter);
      return () => containerElement.removeEventListener("keydown", moveToNextErrorCellOnEnter);
    }
    return undefined;
  }, [moveToNextErrorCellOnEnter]);

  if (!isBrowserCompatible) {
    return <BrowserCompatibilityAlert />;
  }

  return (
    <div className="generic-grid">
      {loading && <LoadingOverlay />}
      <div ref={containerRef}>
        <ReactGrid
          rows={adjustedRows}
          columns={columns}
          onCellsChanged={handleChanges as unknown as (cellChanges: CellChange<DefaultCellTypes | ImageCell>[]) => void}
          customCellTemplates={{ image: ImageCellTemplate }}
          onFocusLocationChanging={setValidFocusLocation}
          focusLocation={focusState.focusLocation || undefined}
        />
      </div>
      <GridControls
        onAdd={() => addRow(data.length)}
        onRemove={() => removeRow(data.length - 1)}
        canRemove={data.length > 1}
      />
    </div>
  );
};

export default GenericGrid;
